<script setup lang="ts">
import { computed, h, nextTick, onMounted, ref, watch, type VNode } from 'vue';
import type { DropdownOption } from 'naive-ui';
import { TemplateFieldType, type TemplateField, type TemplateSchema } from '@/api';
import {
  buildDefaultFormData,
  collectSectionErrors,
  fieldHasInput,
  isTopLevelFieldVisible,
} from '@/composables/contractGeneration/useExtractedSchema';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaStatusIndicator, {
  type GaiaIndicatorStatus,
} from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { gaiaMotion, gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';
import { provideTemplateFieldMetadata } from '@/composables/contractGeneration/useTemplateFieldMetadata';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';
import {
  orderSiblings,
  resolveFieldLabel,
  resolveFieldProse,
} from '@/utils/contractGeneration/templateFieldLabels';
import FieldGuidanceHint from './fields/FieldGuidanceHint.vue';
import FilterHintBadge from './fields/FilterHintBadge.vue';
import SchemaField from './fields/SchemaField.vue';

/**
 * The answer panel of the compose workspace: one section of the template at
 * a time, with a way to see where you are and jump anywhere.
 *
 * The section list used to be a column of its own beside the fields. It is
 * now a menu behind the "Section 3 of 8" line — every section, with its state,
 * one click away — and the width it took goes to the document. A thin
 * progress bar under the title says how much of the template is still open.
 *
 * The whole panel can be filled from the keyboard: Enter steps to the next
 * field and on into the next section, Shift+Enter steps back (see "Keyboard
 * flow" below).
 */

interface Props {
  schema: TemplateSchema;
  /**
   * Authored labels, descriptions, guidance and order per field path. Null
   * renders the template exactly as it always has: humanized keys, document
   * order. Handed to every field below by injection, not props.
   */
  fieldMetadata?: TemplateFieldMetadataMap | null;
  formData: Record<string, unknown>;
  showValidation: boolean;
  disabled?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:formData', value: Record<string, unknown>): void;
}>();

provideTemplateFieldMetadata(() => props.fieldMetadata);

interface SectionInfo {
  key: string;
  label: string;
  /** Authored, or '' — the header falls back to a type-derived line. */
  description: string;
  /** Authored, or '' — offered behind the "?" beside the title. */
  guidance: string;
  field: TemplateField;
  visible: boolean;
  errors: string[];
  /** Holds something beyond its blank default — see `fieldHasInput`. */
  hasInput: boolean;
  touched: boolean;
}

type SectionState = 'current' | 'valid' | 'invalid' | 'pending' | 'skipped';

/** Which sections the user has "attempted to leave" — controls when errors surface. */
const touchedSections = ref<Set<string>>(new Set());
const activeKey = ref<string | null>(null);

const sectionsRaw = computed<SectionInfo[]>(() => {
  // Template order unless an author arranged the sections: Object.entries
  // preserves the backend's document order, and `orderSiblings` overlays the
  // authored positions on top of it.
  const entries = orderSiblings(Object.entries(props.schema.fields ?? {}), '', props.fieldMetadata);
  return entries.map(([key, field]) => {
    const meta = props.fieldMetadata?.[key] ?? null;
    const visible = isTopLevelFieldVisible(field, props.formData);
    const errors = visible ? collectSectionErrors(key, field, props.formData) : [];
    return {
      key,
      label: resolveFieldLabel(meta, key),
      description: resolveFieldProse(meta?.description),
      guidance: resolveFieldProse(meta?.guidance),
      field,
      visible,
      errors,
      hasInput: fieldHasInput(field, props.formData[key]),
      touched: touchedSections.value.has(key),
    };
  });
});

const visibleSections = computed<SectionInfo[]>(() =>
  sectionsRaw.value.filter(section => section.visible)
);

const isSchemaEmpty = computed<boolean>(() => sectionsRaw.value.length === 0);

const allSectionsHidden = computed<boolean>(
  () => !isSchemaEmpty.value && visibleSections.value.length === 0
);

/**
 * A section's state follows its data, not this session's visits: a draft
 * reopened tomorrow shows its filled sections done at once. Errors are the exception — they surface only once the user has been
 * to the section (or asked to generate), so an untouched section is not
 * scolded. A section that is empty and needs nothing is simply pending: a
 * tick there would claim work the user has not done.
 */
function computeState(section: SectionInfo): SectionState {
  if (!section.visible) return 'skipped';
  if (section.key === activeKey.value) return 'current';
  const hasErrors = section.errors.length > 0;
  const visited = section.touched || props.showValidation;
  if (hasErrors) return visited ? 'invalid' : 'pending';
  return section.hasInput || visited ? 'valid' : 'pending';
}

/** How a section's state reads in the jump menu — the indicator's own vocabulary. */
const STATE_INDICATOR: Record<SectionState, { status: GaiaIndicatorStatus; glyph?: 'check' }> = {
  current: { status: 'accent' },
  valid: { status: 'success', glyph: 'check' },
  invalid: { status: 'alert' },
  pending: { status: 'pending' },
  skipped: { status: 'neutral' },
};

/**
 * The jump menu: the sections that apply to this document, in template order,
 * numbered the way the header counts them. A section whose visibility rule is
 * not met is not part of the document right now and is not listed — it
 * appears, renumbered, the moment an earlier answer makes it apply.
 */
const sectionMenuOptions = computed<DropdownOption[]>(() =>
  visibleSections.value.map((section, index) => {
    const indicator = STATE_INDICATOR[computeState(section)];
    return {
      key: section.key,
      label: `${index + 1}. ${section.label}`,
      icon: (): VNode =>
        h(GaiaStatusIndicator, { status: indicator.status, glyph: indicator.glyph }),
    };
  })
);

const currentIndex = computed(() =>
  visibleSections.value.findIndex(section => section.key === activeKey.value)
);

const currentSection = computed<SectionInfo | null>(() => {
  if (currentIndex.value < 0) return visibleSections.value[0] ?? null;
  return visibleSections.value[currentIndex.value] ?? null;
});

const sectionErrorCount = computed(() => currentSection.value?.errors.length ?? 0);

const hasPrevious = computed(() => currentIndex.value > 0);
const hasNext = computed(
  () => currentIndex.value >= 0 && currentIndex.value < visibleSections.value.length - 1
);

/**
 * Progress is measured over the sections that need input — the ones that
 * would block "Generate" while empty. A section with nothing required in it
 * is left out of the count entirely: counting it as done before the user has
 * typed a word read as progress they had not made.
 *
 * Whether a section needs input is found by validating it empty: any error
 * against its blank default means something in it is required. The rest of
 * the form is the live data, so conditions that depend on other answers are
 * honoured.
 */
const blankDefaults = computed(() => buildDefaultFormData(props.schema));

function sectionNeedsInput(section: SectionInfo): boolean {
  const blankFormData = { ...props.formData, [section.key]: blankDefaults.value[section.key] };
  return collectSectionErrors(section.key, section.field, blankFormData).length > 0;
}

const requiredSections = computed(() => visibleSections.value.filter(sectionNeedsInput));
const completedRequiredCount = computed(
  () => requiredSections.value.filter(section => section.errors.length === 0).length
);
const progressPercentage = computed(() =>
  requiredSections.value.length === 0
    ? 100
    : Math.round((completedRequiredCount.value / requiredSections.value.length) * 100)
);
const allRequiredComplete = computed(
  () =>
    requiredSections.value.length > 0 &&
    completedRequiredCount.value === requiredSections.value.length
);
/** The count beside the bar, in the panel's own type: "3 of 8 done". */
const progressCaption = computed(() => {
  if (requiredSections.value.length === 0) return 'Nothing required';
  return `${completedRequiredCount.value} of ${requiredSections.value.length} done`;
});
/** The bar fills in the status green throughout — the colour of "done", the same as the section ticks. */
const progressColor = gaiaStatus.success;

/** The open section has its answer: required or not, nothing in it is still missing and something is filled in. */
const currentSectionDone = computed<boolean>(() => {
  const section = currentSection.value;
  return Boolean(section && section.errors.length === 0 && section.hasInput);
});

/**
 * Which way the next section comes in from. Moving on slides the new section
 * in from the right, going back from the left — the panel reads like pages.
 */
const sectionDirection = ref<'forward' | 'back'>('forward');

watch(
  visibleSections,
  list => {
    if (list.length === 0) {
      activeKey.value = null;
      return;
    }
    if (!activeKey.value || !list.some(section => section.key === activeKey.value)) {
      activeKey.value = list[0].key;
    }
  },
  { immediate: true }
);

function handleFieldUpdate(key: string, value: unknown): void {
  emit('update:formData', { ...props.formData, [key]: value });
}

function markCurrentTouched(): void {
  if (!activeKey.value) return;
  const next = new Set(touchedSections.value);
  next.add(activeKey.value);
  touchedSections.value = next;
}

/** The panel's own scroller — a new section starts at its top. */
const bodyElement = ref<HTMLElement | null>(null);

function showSection(key: string, focus: FocusEdge = 'first'): void {
  pendingFocus.value = focus;
  if (key === activeKey.value) {
    // No transition to wait for.
    void nextTick(applyPendingFocus);
    return;
  }
  markCurrentTouched();
  const targetIndex = visibleSections.value.findIndex(section => section.key === key);
  sectionDirection.value = targetIndex >= currentIndex.value ? 'forward' : 'back';
  activeKey.value = key;
  void nextTick(() => {
    bodyElement.value?.scrollTo({ top: 0 });
  });
}

function jumpToSection(key: string): void {
  if (!sectionsRaw.value.some(section => section.key === key && section.visible)) return;
  showSection(key);
}

function goPrevious(): void {
  const prev =
    currentIndex.value > 0 ? visibleSections.value.at(currentIndex.value - 1) : undefined;
  if (prev) showSection(prev.key);
}

function goNext(): void {
  const next = visibleSections.value.at(currentIndex.value + 1);
  if (next) showSection(next.key);
}

// --- Keyboard flow -------------------------------------------------------
//
// Enter moves to the next field, and from the last field into the next
// section; Shift+Enter walks back the same way. A field's own Enter still
// happens first where it has one: a string or number commits, an open select
// picks its highlighted option (and then moves on), a closed empty select
// opens. Buttons (Add existing, Add item, …) keep their native Enter and are
// reached with Tab, so the flow never opens a dialog on its own.

type FocusEdge = 'first' | 'last';

/** What Enter steps through: text, number and date inputs, and selects. */
const STOP_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, .n-base-selection';
const SELECT = 'n-base-selection';

/** The flow stop an element belongs to — a select's inner input counts as the select. */
function stopOf(el: Element): HTMLElement | null {
  // A tag list's own input adds a tag on Enter.
  if (el.closest('.n-dynamic-tags')) return null;
  const select = el.closest<HTMLElement>(`.${SELECT}`);
  if (select) return select;
  return el instanceof HTMLElement && el.matches(STOP_SELECTOR) ? el : null;
}

function isLiveStop(stop: HTMLElement): boolean {
  if (stop.offsetParent === null || stop.classList.contains(`${SELECT}--disabled`)) return false;
  if (stop instanceof HTMLInputElement || stop instanceof HTMLTextAreaElement) {
    return !stop.disabled && !stop.readOnly;
  }
  return true;
}

/** The current section's stops, in document order. */
function fieldStops(): HTMLElement[] {
  const stops = new Set<HTMLElement>();
  for (const el of bodyElement.value?.querySelectorAll(STOP_SELECTOR) ?? []) {
    const stop = stopOf(el);
    if (stop && isLiveStop(stop)) stops.add(stop);
  }
  return [...stops];
}

function focusStop(stop: HTMLElement): void {
  if (stop.classList.contains(SELECT)) {
    // A filterable select takes typing in its search input, so landing there
    // lets the next keystroke filter; a plain one is focused on its label.
    const target =
      stop.querySelector<HTMLElement>('input.n-base-selection-input') ??
      stop.querySelector<HTMLElement>('[tabindex="0"]') ??
      stop;
    target.focus();
    return;
  }
  stop.focus();
  // Selected, so typing replaces what is there rather than appending to it.
  if (stop instanceof HTMLInputElement || stop instanceof HTMLTextAreaElement) stop.select();
}

/** Where the next section's focus goes once its slide-in has finished. */
const pendingFocus = ref<FocusEdge | null>(null);

function applyPendingFocus(): void {
  const edge = pendingFocus.value;
  pendingFocus.value = null;
  if (!edge) return;
  const stops = fieldStops();
  const stop = edge === 'first' ? stops[0] : stops.at(-1);
  if (stop) focusStop(stop);
}

function moveFocus(from: HTMLElement, step: 1 | -1): void {
  const stops = fieldStops();
  const index = stops.indexOf(from);
  if (index === -1) return;
  const next = stops.at(index + step);
  if (index + step >= 0 && next) {
    focusStop(next);
    return;
  }
  const sectionIndex = currentIndex.value + step;
  const section = sectionIndex >= 0 ? visibleSections.value.at(sectionIndex) : undefined;
  if (!section) return;
  // Blur first: the field commits on blur, and it is about to be unmounted.
  const active = from.ownerDocument.activeElement;
  if (active instanceof HTMLElement) active.blur();
  showSection(section.key, step === 1 ? 'first' : 'last');
}

/**
 * Capture phase, so the decision is made before the field's own handler runs
 * — a select with a value must not reopen its menu when Enter moves on.
 */
function handleFlowKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || event.isComposing) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (!(event.target instanceof Element)) return;
  const stop = stopOf(event.target);
  if (!stop) return;
  const step = event.shiftKey ? -1 : 1;

  if (!stop.classList.contains(SELECT)) {
    event.preventDefault();
    moveFocus(stop, step);
    return;
  }
  if (stop.classList.contains(`${SELECT}--active`)) {
    // The menu is open: the select takes this Enter to pick. Move on once it
    // has closed — a filter that matched nothing leaves it open, and stays.
    if (step === 1) {
      window.setTimeout(() => {
        if (!stop.classList.contains(`${SELECT}--active`)) moveFocus(stop, 1);
      }, 0);
    }
    return;
  }
  // Closed and still empty: let Enter open the menu.
  if (step === 1 && !stop.classList.contains(`${SELECT}--selected`)) return;
  event.preventDefault();
  event.stopPropagation();
  moveFocus(stop, step);
}

// Start in the first field, unless the person is already somewhere else.
onMounted(() => {
  void nextTick(() => {
    if (document.activeElement && document.activeElement !== document.body) return;
    pendingFocus.value = 'first';
    applyPendingFocus();
  });
});

const invalidPaths = computed<Set<string>>(() => {
  const paths = new Set<string>();
  for (const section of sectionsRaw.value) {
    const surface = section.touched || props.showValidation;
    if (!surface) continue;
    for (const path of section.errors) paths.add(path);
  }
  return paths;
});

const sectionDescription = computed<string>(() => {
  if (!currentSection.value) return '';
  // What the author wrote wins; the type-derived line is the fallback.
  if (currentSection.value.description) return currentSection.value.description;
  const field = currentSection.value.field;
  if (field.type === TemplateFieldType.LEGAL_ENTITY) {
    return 'Pick from your legal entity directory or fill in the fields manually.';
  }
  if (field.type === TemplateFieldType.NATURAL_PERSON) {
    return 'Pick from your natural person directory or fill in the fields manually.';
  }
  if (field.type === TemplateFieldType.OBJECT) {
    const count = Object.keys(field.fields ?? {}).length;
    return `${count} field${count === 1 ? '' : 's'} in this section.`;
  }
  if (field.type === TemplateFieldType.LIST) {
    if (
      field.item_type === TemplateFieldType.LEGAL_ENTITY ||
      field.item_type === TemplateFieldType.NATURAL_PERSON
    ) {
      const label =
        field.item_type === TemplateFieldType.LEGAL_ENTITY ? 'legal entities' : 'natural persons';
      return `Add as many ${label} as the template needs — pick or fill in each.`;
    }
    return field.item_fields ? 'Add as many entries as you need.' : 'Add any number of values.';
  }
  return '';
});

/**
 * A top-level field renders with its header hidden (this pane shows it), so
 * its guidance has to be offered here too — the "?" beside the title.
 */
const sectionGuidance = computed<string>(() => currentSection.value?.guidance ?? '');

// --- chrome (tokens for the scoped styles) ---
const gRule = gaiaNeutral.borderDivider;
const gSoft = gaiaNeutral.surfaceSoft;
const gHover = gaiaNeutral.surfaceHover;
const gInk = gaiaNeutral.ink;
const gSurface = gaiaNeutral.surface;
const gTrack = gaiaNeutral.surfaceMuted;
const gSuccess = gaiaStatus.success;
const easeOut = gaiaMotion.easeOut;
const dQuick = gaiaMotion.quick;
const dStandard = gaiaMotion.standard;
</script>

<template>
  <!-- The three empty states fill the panel on their own; otherwise the panel
       is head / scrolling fields / pinned Back-Next. -->
  <div class="fields-panel">
    <n-flex
      v-if="isSchemaEmpty || allSectionsHidden"
      align="center"
      justify="center"
      :style="{ flex: 1, padding: '24px' }"
    >
      <gaia-empty-state
        v-if="isSchemaEmpty"
        dashed
        status="warning"
        title="No fields detected in this template"
      >
        We couldn't find any placeholders to fill in. Open the source .docx and confirm it contains
        placeholders, or re-upload the template to re-run the extraction.
      </gaia-empty-state>
      <gaia-empty-state v-else dashed title="No sections to fill in right now">
        Every section in this template is currently hidden by its visibility rules. Adjust earlier
        answers to reveal more sections, or generate the document as-is.
      </gaia-empty-state>
    </n-flex>

    <template v-else-if="currentSection">
      <header class="fields-panel__head">
        <n-flex align="center" justify="space-between" :size="8" :wrap="false">
          <!-- "Section 3 of 8" is the jump menu's trigger: the counter says where
               you are, the chevron says it opens. Scrollable with a cap: a long
               template has dozens of sections and the menu must stay on screen. -->
          <gaia-dropdown
            :options="sectionMenuOptions"
            placement="bottom-start"
            scrollable
            :style="{ maxHeight: '60vh' }"
            @select="jumpToSection"
          >
            <button type="button" class="section-picker" aria-label="Jump to a section">
              <gaia-text variant="label" size="micro" tone="subtle">
                Section {{ currentIndex + 1 }} of {{ visibleSections.length }}
              </gaia-text>
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M2.5 4.5 6 8l3.5-3.5" />
              </svg>
            </button>
          </gaia-dropdown>
          <!-- "Required" turns into a green "Complete" the moment the section
               has its answer — the pill swaps with a small pop. -->
          <transition name="pop" mode="out-in">
            <gaia-tag
              v-if="currentSection.field.required && currentSectionDone"
              key="complete"
              type="success"
              size="tiny"
              round
            >
              Complete
            </gaia-tag>
            <gaia-tag
              v-else-if="currentSection.field.required"
              key="required"
              variant="amber"
              size="tiny"
            >
              Required
            </gaia-tag>
          </transition>
        </n-flex>

        <!-- Title and description slide with the section, the way the fields
             below do, so the whole panel turns like a page. -->
        <transition :name="`section-${sectionDirection}`" mode="out-in">
          <div :key="currentSection.key" class="fields-panel__title">
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-header
                :level="2"
                size="dialog"
                :style="{ minWidth: 0, overflowWrap: 'anywhere' }"
              >
                {{ currentSection.label }}
              </gaia-header>
              <field-guidance-hint v-if="sectionGuidance" :text="sectionGuidance" />
              <!-- A section that is one field draws its label here, so its
                   formatting wand sits at the right of this row. -->
              <span :style="{ marginLeft: 'auto', display: 'inline-flex' }">
                <filter-hint-badge :filters="currentSection.field.filters" />
              </span>
            </n-flex>
            <gaia-text
              v-if="sectionDescription"
              size="meta"
              tone="subtle"
              :style="{ lineHeight: 1.5 }"
            >
              {{ sectionDescription }}
            </gaia-text>
          </div>
        </transition>

        <!-- Progress in the panel's own type — a label, the count, and a real
             track under them. The bar grows with each answered section; when
             the last one lands a light sweeps across it once and a green line
             says the document is ready. -->
        <n-flex vertical :size="6" :wrap="false" :style="{ marginTop: '8px' }">
          <n-flex align="center" justify="space-between" :size="8" :wrap="false">
            <gaia-text size="micro" weight="semibold" tone="text">Required fields</gaia-text>
            <gaia-text size="micro" tone="subtle">{{ progressCaption }}</gaia-text>
          </n-flex>
          <div
            class="fields-panel__bar"
            :class="{ 'fields-panel__bar--complete': allRequiredComplete }"
          >
            <gaia-progress-bar
              :percentage="progressPercentage"
              :show-indicator="false"
              :height="6"
              :border-radius="999"
              :color="progressColor"
              :rail-color="gTrack"
            />
          </div>
        </n-flex>
        <transition name="pop">
          <n-flex v-if="allRequiredComplete" align="center" :size="6" :wrap="false">
            <gaia-status-indicator status="success" glyph="check" />
            <gaia-text size="micro" weight="semibold" :style="{ color: gSuccess }">
              All required sections complete — ready to generate
            </gaia-text>
          </n-flex>
        </transition>
      </header>

      <!-- `section-body` is what the guided tour looks for; keep the class. -->
      <div
        ref="bodyElement"
        class="fields-panel__body section-body"
        @keydown.capture="handleFlowKeydown"
      >
        <transition
          :name="`section-${sectionDirection}`"
          mode="out-in"
          @after-enter="applyPendingFocus"
        >
          <div :key="currentSection.key">
            <gaia-banner
              v-if="(currentSection.touched || showValidation) && sectionErrorCount > 0"
              plain
              type="warning"
              :style="{ marginBottom: '14px' }"
            >
              {{ sectionErrorCount }} required field{{ sectionErrorCount === 1 ? '' : 's' }} still
              need{{ sectionErrorCount === 1 ? 's' : '' }} attention.
            </gaia-banner>

            <SchemaField
              :field-key="currentSection.key"
              :field="currentSection.field"
              :model-value="formData[currentSection.key]"
              :root-form-data="formData"
              :local-scopes="[formData]"
              :path="currentSection.key"
              :invalid-paths="invalidPaths"
              :disabled="disabled"
              top-level
              hide-container-header
              @update:model-value="value => handleFieldUpdate(currentSection!.key, value)"
            />
          </div>
        </transition>
      </div>

      <footer class="fields-panel__foot">
        <gaia-button size="small" :disabled="!hasPrevious || disabled" @click="goPrevious">
          Back
        </gaia-button>
        <gaia-text v-if="hasNext && !disabled" size="micro" tone="faint">
          Enter for next · Shift+Enter back
        </gaia-text>
        <gaia-button
          v-if="hasNext"
          type="primary"
          size="small"
          :disabled="disabled"
          @click="goNext"
        >
          Next section
        </gaia-button>
        <gaia-text v-else-if="!disabled" size="micro" tone="subtle" :style="{ textAlign: 'right' }">
          Last section. Generate the document from the bar below when it looks right.
        </gaia-text>
      </footer>
    </template>
  </div>
</template>

<style scoped>
/* Head and foot pinned, the fields scrolling between them: the column's
   height comes from the compose pane, and only the body may grow past it. */
.fields-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.fields-panel__head {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 18px 12px;
  border-bottom: 1px solid v-bind(gRule);
  background: v-bind(gSoft);
}

.fields-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 18px 20px;
}

.fields-panel__foot {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 18px;
  border-top: 1px solid v-bind(gRule);
  background: v-bind(gSoft);
}

/* The counter as a button: text-shaped, with a hover so it reads as a control. */
.section-picker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  margin-left: -6px;
  border: none;
  border-radius: 6px;
  background: none;
  color: v-bind(gInk);
  font-family: inherit;
  cursor: pointer;
}

.section-picker:hover {
  background: v-bind(gHover);
}

.fields-panel__title {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* ─── Motion ────────────────────────────────────────────────────────────
   A section turns like a page: the leaving one slips out the way you are
   going, the next slides in from the other side. Pills and the completion
   line pop in a touch past their size and settle. */
.section-forward-enter-active,
.section-back-enter-active {
  transition:
    opacity v-bind(dStandard) v-bind(easeOut),
    transform v-bind(dStandard) v-bind(easeOut);
}

.section-forward-leave-active,
.section-back-leave-active {
  transition:
    opacity v-bind(dQuick) ease,
    transform v-bind(dQuick) ease;
}

.section-forward-enter-from {
  opacity: 0;
  transform: translateX(16px);
}

.section-forward-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.section-back-enter-from {
  opacity: 0;
  transform: translateX(-16px);
}

.section-back-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

.pop-enter-active {
  animation: pop-in v-bind(dStandard) v-bind(easeOut);
}

.pop-leave-active {
  transition: opacity v-bind(dQuick) ease;
}

.pop-leave-to {
  opacity: 0;
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  60% {
    transform: scale(1.04);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* The track, and its finishing sweep: a soft band of the surface colour
   crosses the bar once when the last required section lands. */
.fields-panel__bar {
  position: relative;
  overflow: hidden;
  border-radius: 999px;
}

.fields-panel__bar--complete::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, v-bind(gSurface), transparent);
  opacity: 0.65;
  transform: translateX(-100%);
  animation: progress-sweep 1.1s v-bind(easeOut) 1 both;
  pointer-events: none;
}

@keyframes progress-sweep {
  to {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .section-forward-enter-active,
  .section-back-enter-active,
  .section-forward-leave-active,
  .section-back-leave-active,
  .pop-leave-active {
    transition: none;
  }

  .pop-enter-active,
  .fields-panel__bar--complete::after {
    animation: none;
  }
}
</style>
